import React from 'react';
import { Button } from 'antd';
import { CreditCardOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { paymentMethodLabel } from '../../../services/tenant/providerBilling';

/**
 * The partner's billing card, as the last step of the paywall shows it: the
 * card on file with a way to swap it, or — straight back from Stripe, before
 * its webhook has delivered the card — a "confirming" row that offers a
 * re-check instead of a second trip through Checkout.
 *
 * `paymentMethod` is `{ brand, last4 }` from the entitlements payload; an older
 * backend sends only `payment_method_on_file`, and the row then names no card
 * rather than inventing one.
 */
export default function PaywallPaymentMethod({
  paymentMethod = null,
  pending = false,
  checking = false,
  stillMissing = false,
  changing = false,
  onChange = () => {},
  onCheck = () => {},
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:ProviderBilling:LicensePaywallModal' });

  if (pending) {
    return (
      <div
        className="flex flex-col gap-xs rounded-xl border border-solid border-brand-inkGrey-grey_2 py-sm pl-md pr-sm"
        data-testid="license-paywall-payment-method-pending"
        role="status"
      >
        <div className="flex items-center gap-sm">
          <LoadingOutlined className="text-[20px] text-brand-text-default" aria-hidden />
          <div className="flex flex-col gap-nano">
            <span className="text-[15px] font-semibold text-brand-text-default">
              {t('payment_method.pending_title')}
            </span>
            <span className="text-[13px] text-brand-inkGrey-grey_3">{t('payment_method.pending')}</span>
          </div>
        </div>
        {stillMissing && (
          <span className="text-[13px] text-brand-inkGrey-grey_3" data-testid="license-paywall-card-still-missing">
            {t('payment_method.still_pending')}
          </span>
        )}
        <div className="flex flex-wrap gap-xs">
          <Button size="small" loading={checking} onClick={onCheck} data-testid="license-paywall-check-card">
            {t('buttons.check_again')}
          </Button>
          <Button
            size="small"
            type="text"
            className="font-semibold underline"
            loading={changing}
            onClick={onChange}
            data-testid="license-paywall-add-card"
          >
            {t('buttons.add_again')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex items-center justify-between gap-sm rounded-xl border border-solid border-brand-inkGrey-grey_2 py-sm pl-md pr-sm"
      data-testid="license-paywall-payment-method"
    >
      <div className="flex items-center gap-sm">
        <CreditCardOutlined className="text-[22px] text-brand-text-default" aria-hidden />
        <div className="flex flex-col gap-nano">
          <span className="text-[15px] font-semibold text-brand-text-default" data-testid="license-paywall-card">
            {paymentMethodLabel(paymentMethod) ?? t('payment_method.on_file')}
          </span>
          <span className="text-[13px] text-brand-inkGrey-grey_3">{t('payment_method.label')}</span>
        </div>
      </div>
      <Button
        type="text"
        className="font-semibold underline"
        loading={changing}
        onClick={onChange}
        data-testid="license-paywall-change-card"
      >
        {t('buttons.change')}
      </Button>
    </div>
  );
}
